import {
  Button,
  IconButton,
  Table,
  TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow,
  Tooltip, Typography
} from '@material-ui/core';
import {
  AlertIcon, AssembleiaIcon, CommunityIcon, DialogIcon,
  InvertedChevronIcon, PeopleIcon, SyncIcon
} from 'components/Icons';
import {
  AssembleiaAssessorRoutePath,
  MensagensAssessorRoutePath,
  ConvocadosAssessorRoutePath,
  RelatorioPresentesRoutePath
} from 'library/Routes';
import { ListaEventoRequest } from 'models/ListaEventoRequest';
import moment from 'moment';
import React, { useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { assembleiasAssessorActionsLoadEventoPageList } from 'store/modules/AssembleiasAssessorActions';
import { IReducer } from 'store/Reducer';
import AxiosErrorHandler from 'utils/AxiosErrorHandler';
import { tableInitialState, tableReducer } from './reducer';
import useStyle from './style';
import Description from './Descripition';

interface Props {
  filters: Partial<ListaEventoRequest> | undefined;
  hasDateError: boolean;
}

export default function HomeTable({ filters, hasDateError }: Props) {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const history = useHistory();
  const { usuario } = useSelector((state: IReducer) => state.auth);
  const { loadingListaPaginadaEvento } = useSelector((state: IReducer) => state.assembleiasAssessor);
  const [{ rows, page, count }, action] = useReducer(tableReducer, tableInitialState);

  const heads = useMemo(
    () => [
      t('TelaInicialAssessor.tableHead.codigo'),
      t('TelaInicialAssessor.tableHead.produto'),
      t('TelaInicialAssessor.tableHead.descricao'),
      t('TelaInicialAssessor.tableHead.tipo'),
      t('TelaInicialAssessor.tableHead.status'),
      t('TelaInicialAssessor.tableHead.dataInicial'),
      t('TelaInicialAssessor.tableHead.dataFinal'),
      t('TelaInicialAssessor.tableHead.acoes'),
    ],
    [t]
  );

  const pages = useMemo(() => {
    const arr = [];
    let current = 0;
    while (arr.length < count) {
      current += 1;
      arr.push(current);
    }
    return arr;
  }, [count]);

  const irParaPagina = (value: number) => {
    action({
      type: 'setPage',
      value,
    });
  };

  useEffect(() => {
    if (!filters) return;
    dispatch(
      assembleiasAssessorActionsLoadEventoPageList(
        { ...filters, page, size: 12 },
        listagem => {
          action({ type: 'setRows', value: listagem.elements });
          action({ type: 'setCount', value: listagem.totalPages });
        },
        AxiosErrorHandler
      )
    );
  }, [dispatch, filters, page]);

  useEffect(() => {
    action({
      type: 'setPage',
      value: 0,
    });
  }, [count]);

  if (hasDateError) {
    return (
      <TableContainer className={classes.root}>
        <Typography component="p" className={classes.advice}>
          <AlertIcon />
          {t('ListaLogins.dateErrorMessage')}
        </Typography>
      </TableContainer>
    );
  }

  if (loadingListaPaginadaEvento) {
    return (
      <TableContainer className={classes.root}>
        <Typography component="p" className={classes.advice}>
          <SyncIcon animated />
          {t('TelaInicialAssessor.tableLoading')}
        </Typography>
      </TableContainer>
    );
  }

  if (!rows || !rows.length) {
    return (
      <TableContainer className={classes.root}>
        <Typography component="p" className={classes.advice}>
          <AlertIcon />
          {t('TelaInicialAssessor.empty')}
        </Typography>
      </TableContainer>
    );
  }

  return (
    <TableContainer className={classes.root}>
      <Table>
        <TableHead className={classes.box}>
          <TableRow>
            {heads.map(head => (
              <TableCell key={head} align="center" className={classes.title}>
                {head}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody className={classes.box}>
          {rows.map((row, key) => (
            <TableRow key={key}>
              <TableCell align="center" className={classes.data}>
                {row.codigo}
              </TableCell>
              <TableCell align="center" className={classes.data}>
                <span dangerouslySetInnerHTML={{ __html: row.produto.split(',').join('<br />') }} />
              </TableCell>
              <TableCell align="center" className={classes.data}>
                <Description text={row.descricaoEvento} maxChars={100} fontSize={12} />
              </TableCell>
              <TableCell align="center" className={classes.data}>
                {row.tipoEvento}
              </TableCell>
              <TableCell align="center" className={classes.data}>
                {t('AssembleiaStatus', { returnObjects: true })[row.status]}
              </TableCell>
              <TableCell align="center" className={classes.data}>
                {moment(row.dataInicial).format('DD/MM/YYYY')}
              </TableCell>
              <TableCell align="center" className={classes.data}>
                {moment(row.dataFinal).format('DD/MM/YYYY')}
              </TableCell>
              <TableCell align="center" className={classes.actions}>
                <Tooltip title={t('TelaInicialAssessor.actionEvento') as string} arrow>
                  <span>
                    <IconButton
                      color="primary"
                      className={classes.action}
                      disabled={!usuario?.perfil.assessorRoute.assembleias.ver}
                      onClick={() => history.push(AssembleiaAssessorRoutePath.replace(':id', row.codigo.toString()))}
                    >
                      <AssembleiaIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('TelaInicialAssessor.actionConvocados') as string} arrow>
                  <span>
                    <IconButton
                      color="primary"
                      className={classes.action}
                      disabled={!usuario?.perfil.assessorRoute.convocados.view}
                      onClick={() => history.push(ConvocadosAssessorRoutePath.replace(':id', row.codigo.toString()))}
                    >
                      <CommunityIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('TelaInicialAssessor.actionPresentes') as string} arrow>
                  <span>
                    <IconButton
                      color="primary"
                      className={classes.action}
                      disabled={!usuario?.perfil.assessorRoute.assembleias.participantes.verTotal}
                      onClick={() =>
                        history.push(RelatorioPresentesRoutePath.replace(':idAssembleia', row.codigo.toString()))
                      }
                    >
                      <PeopleIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('TelaInicialAssessor.actionMensagens') as string} arrow>
                  <span>
                    <IconButton
                      color="primary"
                      className={classes.action}
                      disabled={!usuario?.perfil.assessorRoute.duvidasSugestoes.ver}
                      onClick={() => history.push(MensagensAssessorRoutePath.replace(':idEvento', row.codigo.toString()))}
                    >
                      <DialogIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={9} className={classes.footer}>
              <nav className={classes.paginacao}>
                {count > 0 && page > 0 && (
                  <IconButton onClick={() => irParaPagina(page - 1)} color="primary">
                    <InvertedChevronIcon className={classes.paginacaoPrev} />
                  </IconButton>
                )}
                {pages.map(current => (
                  <Button
                    variant="text"
                    key={current}
                    onClick={() => irParaPagina(current - 1)}
                    disabled={current - 1 === page}
                    color="primary"
                    className={classes.paginacaoNumber}
                  >
                    {current}
                  </Button>
                ))}
                {page < count - 1 && (
                  <IconButton onClick={() => irParaPagina(page + 1)} color="primary">
                    <InvertedChevronIcon className={classes.paginacaoNext} />
                  </IconButton>
                )}
              </nav>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
